import PropTypes from 'prop-types';
import { Trophy, Target, Clock, BarChart2, MapPin, Lightbulb } from 'lucide-react';
import Button from '../ui/Button';
import { useAnimatedNumber } from '../../hooks/useAnimatedNumber';
import './GameResults.css';

const getPerformance = (score, maxScore) => {
  const pct = score / maxScore;
  if (pct >= 0.85) return { label: 'Excellent', cls: 'excellent', Icon: Trophy };
  if (pct >= 0.65) return { label: 'Great', cls: 'great', Icon: Target };
  return { label: 'Good Effort', cls: 'good', Icon: BarChart2 };
};

const formatTime = (seconds) => {
  const safe = Math.max(0, Math.round(seconds));
  return `${Math.floor(safe / 60)}:${(safe % 60).toString().padStart(2, '0')}`;
};

const RoundScore = ({ value }) => {
  const animated = useAnimatedNumber(value, { duration: 1200, delay: 300 });
  return <>{animated.toLocaleString()}</>;
};

RoundScore.propTypes = { value: PropTypes.number.isRequired };

const GameResults = ({ results, onPlayAgain, onViewLeaderboard }) => {
  const totalScore = results.total_score || 0;
  const rounds = results.rounds || [];
  const userRank = results.leaderboard_position || null;
  const maxScore = rounds.length * 1000;

  const perf = getPerformance(totalScore, maxScore);

  const avgDistRaw = rounds.length
    ? rounds.reduce((s, r) => s + parseFloat(r.distance_km || 0), 0) / rounds.length
    : 0;
  const totalTime = rounds.reduce((s, r) => s + (r.time_taken || 0), 0);

  const animScore     = useAnimatedNumber(totalScore, { duration: 1800, delay: 200 });
  const animRounds    = useAnimatedNumber(rounds.length, { duration: 900, delay: 500 });
  const animAvgDist   = useAnimatedNumber(avgDistRaw, { duration: 1400, decimals: 1, delay: 500 });
  const animTime      = useAnimatedNumber(totalTime, { duration: 1400, delay: 500 });
  const animRank      = useAnimatedNumber(userRank || 0, { duration: 1200, delay: 600 });

  return (
    <div className="game-results">
      <div className="results-container">

        {/* Score hero */}
        <div className="results-hero">
          <span className="results-eyebrow">Game Complete</span>
          <h1 className="results-title">Your Score</h1>

          <div className="total-score">
            <span className="total-score-number">{animScore.toLocaleString()}</span>
            <span className="total-score-denom">/ {maxScore.toLocaleString()} pts</span>
          </div>

          <div className={`perf-badge ${perf.cls}`}>
            <perf.Icon size={13} />
            {perf.label}
          </div>

          {userRank && <div className="rank-info">Leaderboard Rank #{animRank}</div>}
        </div>

        {/* Stats strip */}
        <div className="results-stats">
          <div className="results-stat">
            <span className="results-stat-num">{animRounds}</span>
            <span className="results-stat-label">Rounds</span>
          </div>
          <div className="results-stat">
            <span className="results-stat-num">{animAvgDist.toFixed(1)} km</span>
            <span className="results-stat-label">Avg Distance</span>
          </div>
          <div className="results-stat">
            <span className="results-stat-num">{formatTime(animTime)}</span>
            <span className="results-stat-label">Total Time</span>
          </div>
        </div>

        {/* Rounds table */}
        <div className="rounds-section">
          <div className="rounds-section-header">Round Breakdown</div>
          <table className="results-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Location</th>
                <th><Clock size={12} style={{ verticalAlign: 'middle' }} /></th>
                <th>Score</th>
              </tr>
            </thead>
            <tbody>
              {rounds.map((round, index) => (
                <tr key={index}>
                  <td className="round-num">{round.round_number}</td>
                  <td className="round-loc">
                    {round.location_name || round.location?.name || 'Unknown'}
                    <div className="round-dist">{parseFloat(round.distance_km || 0).toFixed(2)} km away</div>
                  </td>
                  <td>
                    {round.time_taken ? formatTime(round.time_taken) : '—'}
                  </td>
                  <td className="score-cell">
                    <RoundScore value={round.score || 0} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Locations explored — educational recap */}
        {rounds.some((r) => r.description || r.facts?.length > 0) && (
          <div className="edu-section">
            <div className="rounds-section-header">Locations Explored</div>
            <div className="edu-list">
              {rounds.map((round, index) => {
                if (!round.description && !(round.facts?.length > 0)) return null;
                return (
                  <div key={index} className="edu-item">
                    <div className="edu-item-head">
                      <MapPin size={14} />
                      <span className="edu-item-name">
                        {round.location_name || round.location?.name || 'Unknown'}
                      </span>
                    </div>
                    {round.description && (
                      <p className="edu-item-desc">{round.description}</p>
                    )}
                    {round.facts?.length > 0 && (
                      <ul className="edu-item-facts">
                        {round.facts.map((fact, i) => (
                          <li key={i}>
                            <Lightbulb size={12} />
                            <span>{fact}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="results-actions">
          <Button onClick={onPlayAgain} variant="primary" size="lg">
            Play Again
          </Button>
          <Button onClick={onViewLeaderboard} variant="secondary" size="lg">
            Leaderboard
          </Button>
        </div>

      </div>
    </div>
  );
};

GameResults.propTypes = {
  results: PropTypes.shape({
    total_score: PropTypes.number,
    rounds: PropTypes.arrayOf(PropTypes.object),
    leaderboard_position: PropTypes.number,
  }).isRequired,
  onPlayAgain: PropTypes.func.isRequired,
  onViewLeaderboard: PropTypes.func.isRequired,
};

export default GameResults;
